import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { NgClass } from "@angular/common";
import { Component, computed, effect, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterOutlet } from "@angular/router";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutAlignDirective, LayoutDirective, FlexFillDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeDrawerModule, ForgeModalDrawerModule } from "@tylertech/forge-angular";
import { ShellService } from "@tylertech/incode-module";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { debounceTime } from "rxjs/operators";
import { NavMenuService, OmniBarService, SecurityService, ThemeService, TylerAssistantSidebarService } from "../../services";
import { AppMenuComponent } from "../appMenu/appMenu.component";
import { OmniBarComponent } from "../omniBar/omniBar.component";
import { TylerAssistantUnavailableComponent } from "../omniBar/smartPulse/tylerAssistantUnavailable.component";

export const enum DrawerMode
{
    Modal = "modal",
    Dismissible = "dismissible",
}

@Component({
    selector: "app-outlet",
    templateUrl: "./app-outlet.tmpl.html",
    styleUrls: ["./app-outlet.component.scss"],
    imports: [
        AppMenuComponent,
        ClassDirective,
        LayoutAlignDirective,
        LayoutDirective,
        FlexFillDirective,
        ForgeDrawerModule,
        ForgeModalDrawerModule,
        NgClass,
        OmniBarComponent,
        RouterOutlet,
        TylerAssistantUnavailableComponent,
    ]
})
export class AppOutletComponent implements OnInit
{
    private readonly $navMenuService = inject(NavMenuService);
    private readonly $shellService = inject(ShellService);
    private readonly $tylerAssistantSidebarService = inject(TylerAssistantSidebarService);

    protected readonly showAssistantFallback = computed(() => this.$tylerAssistantSidebarService.foundryUnavailable() && this.$tylerAssistantSidebarService.fallbackOpen());

    private readonly log = getLoggerFor("AppOutletComponent");
    protected menuMode: DrawerMode;
    menuOpen = false;
    showOmni: boolean;
    embedded: boolean = false;
    isProvisional: boolean = false;
    constructor()
    {
        const breakpointObserver = inject(BreakpointObserver);
        const $omniBarService = inject(OmniBarService);
        const $navMenuService = this.$navMenuService;
        const $securityService = inject(SecurityService);
        const $themeService = inject(ThemeService);

        breakpointObserver.observe("(max-width: 1439px)")
            .pipe(debounceTime(500), takeUntilDestroyed())
            .subscribe((breakpointState: BreakpointState) =>
            {
                if (breakpointState.matches)
                {
                    this.menuMode = DrawerMode.Modal;
                    this.$navMenuService.closeMenu();
                }
                else
                {
                    this.menuMode = DrawerMode.Dismissible;
                    if (this.$navMenuService.IsNavPinned && !this.$navMenuService.IsMenuOpen)
                    {
                        this.$navMenuService.toggle(true);
                    }
                }
            });

        $navMenuService.toggleChange
            .pipe(takeUntilDestroyed())
            .subscribe((newValue: boolean) =>
            {
                if ($securityService.isAuthenticated)
                {
                    this.toggleMenu(newValue);
                }
            });

        $navMenuService.disabled
            .pipe(takeUntilDestroyed())
            .subscribe(disabled =>
            {
                if (disabled)
                {
                    this.closeMenu();
                }
            });

        $omniBarService.showOmnibar
            .pipe(takeUntilDestroyed())
            .subscribe((show: boolean) =>
            {
                this.showOmni = show;
            });

        $securityService.principleChange
            .pipe(takeUntilDestroyed())
            .subscribe((principle) =>
            {
                this.$navMenuService.setDisabled(!principle);

                if (!$securityService.isAuthenticated)
                {
                    this.menuOpen = false;
                }

                if (this.isModal)
                {
                    this.$navMenuService.closeMenu();
                }

                this.isProvisional = $securityService.isProvisional;
            });

        effect(() =>
        {
            this.log.debug(`Theme changed`, { theme: $themeService.theme() });
        });
    }

    ngOnInit(): void
    {
        this.embedded = this.$shellService.isEmbedded();
    }

    get isModal(): boolean { return this.menuMode === DrawerMode.Modal; }

    get scaffoldClasses(): Record<string, boolean>
    {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            "nav-open": this.$navMenuService.IsNavPinned,
            "provisional": this.isProvisional,
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    }

    get leftMenuTabIndex(): number
    {
        return this.$navMenuService.IsNavPinned || this.menuOpen ? 0 : -1;
    }

    @LogInvocation(LogLevel.DEBUG)
    protected toggleMenu(opened: boolean): void
    {
        this.menuOpen = opened;
    }

    @LogInvocation(LogLevel.DEBUG)
    closeMenu(): void
    {
        if (!this.isModal)
        {
            return;
        }

        this.$navMenuService.toggle();
    }
}
